import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { SwitcherItemViewModel } from "../../view-models/switcher-item.view-model";
import { IBroadcastsRepository } from "app/js/src/domain/broadcasts/i-broadcasts-repository";
import {
  assetBlockItemExtendedData,
  broadcastAssetItemExtendedData,
  AssetGroupEntry,
} from "app/js/src/domain/broadcasts/asset-block-item-extended-data";

const GROUP_COLORS = [
  '#5b9bd5',
  '#e6a23c',
  '#8e6fc9',
  '#d95f6a',
  '#4bacc6',
  '#7b9e5c',
];
import { Subscription } from "rxjs";
import { UserService } from "app/js/src/domain/user-service";
import { Channel } from "app/js/src/domain/channel";
import moment from "moment";

interface PlaylistItem {
  id: string;
  name: string;
  playTime?: string;
  duration: string;
  playedPercentage?: string;
  isSelected?: boolean;
  thumbnail?: string;
}

@Component({
  selector: "broadcast-switch-item-block-manager",
  templateUrl: "./switch-item-block-manager.component.html",
  styleUrl: "./switch-item-block-manager.component.scss",
})
export class SwitchItemBlockManagerComponent implements OnInit, OnDestroy {
  @Input() vm: SwitcherItemViewModel;
  @Input() broadcastUid: string;
  @Input() loading: boolean = true;
  @Output() onPreviewClicked = new EventEmitter<MouseEvent>();
  @Output() onProgramClicked = new EventEmitter<MouseEvent>();
  @Output() changeBlockType = new EventEmitter<void>();
  @ViewChild("tableBody", { static: false }) tableBody: ElementRef;

  selectedItem?: PlaylistItem;
  draggingItem: PlaylistItem | null = null;
  playlistItems: PlaylistItem[] = [];
  private stackIndex: number = 0;
  private dataChangeSubscription: Subscription | null = null;
  channelTimezone: string;
  private readonly onNativeDocumentClick = () => {
    if (this.showGroupContextMenu) {
      this._zone.run(() => this.hideGroupContextMenu());
    }
  };

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _broadcastsPresetsRepository: IBroadcastsRepository,
    private _userService: UserService,
    private _elementRef: ElementRef,
    private _zone: NgZone,
  ) {}

  async ngOnInit() {
    this._userService.getCurrentChannel().then((channel) => {
      this.channelTimezone = this._userService.getTimezoneValueById(
        channel.timezoneId,
      );
      this.initializePlaylistItems();
    });

    this.subscribeToDataChanges();

    // Use the ownerDocument of this component's host element so the listener
    // works whether the component is rendered in the main window or a popup.
    const doc = this._elementRef.nativeElement.ownerDocument as Document;
    doc.addEventListener('click', this.onNativeDocumentClick);
  }

  ngOnDestroy(): void {
    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }

    const doc = this._elementRef.nativeElement.ownerDocument as Document;
    doc.removeEventListener('click', this.onNativeDocumentClick);
  }

  private subscribeToDataChanges(): void {
    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }

    if (this.vm && this.vm.dataChanged$) {
      this.dataChangeSubscription = this.vm.dataChanged$.subscribe(() => {
        this.initializePlaylistItems();
      });
    }
  }

  private async initializePlaylistItems() {
    const data = this.vm.data as assetBlockItemExtendedData | null;
    if (!data?.assets) {
      this.playlistItems = [];
      this.selectedItem = undefined;
      return;
    }
    this.playlistItems = data.assets.map((item) => {
      return {
        id: item.id,
        name: item.name,
        duration: this.formatDuration(item.duration),
        thumbnail: item.thumbnail,
      };
    });
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);

    if (
      this.vm.isStack &&
      extendedData.assetsOrder &&
      Object.entries(extendedData.assetsOrder).length > 0
    ) {
      this.playlistItems.sort((a, b) => {
        return extendedData.assetsOrder[a.id] - extendedData.assetsOrder[b.id];
      });
    }
    for (const item of extendedData.assetsAsPlayedLog) {
      const playlistItem = this.playlistItems.find((i) => i.id === item.id);
      if (playlistItem) {
        const asset = data.assets.find((a) => a.id === item.id);
        if (asset) {
          // Convert Unix timestamp (seconds) to "dd.mm.yy hh:mm.ss" format
          const date = moment.tz(new Date(item.airTimestamp * 1000), this.channelTimezone);
          playlistItem.playTime = date.format("DD.MM.YY HH:mm.ss");
          playlistItem.playedPercentage = `${Math.floor((item.duration / asset.duration) * 100)}%`;
        }
      }
    }
    const rowToSelect = this.playlistItems[extendedData.stackIndex];
    if (rowToSelect) {
      this.selectItem(rowToSelect);
    } else {
      this.selectedItem = undefined;
    }
    this.stackIndex = extendedData.stackIndex;
  }

  get isStackEditingDisabled(): boolean {
    return !!this.vm?.isStack && !!this.vm?.isProgram;
  }

  // Drag and Drop Methods
  onDragStart(event: DragEvent, item: PlaylistItem) {
    if (this.isStackEditingDisabled) {
      event.preventDefault();
      return;
    }
    this.draggingItem = item;
    if (event.target instanceof HTMLElement) {
      event.target.classList.add("dragging");
    }
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", item.id);
  }

  onDragEnd(event: DragEvent) {
    if (event.target instanceof HTMLElement) {
      event.target.classList.remove("dragging");
    }
    this.draggingItem = null;
    // Clear all drop indicators with a small delay to ensure proper cleanup
    setTimeout(() => {
      this.clearDropIndicators();
    }, 10);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  onDragEnter(event: DragEvent, targetItem: PlaylistItem) {
    if (this.isStackEditingDisabled) {
      return;
    }
    if (!this.draggingItem || this.draggingItem.id === targetItem.id) {
      return;
    }

    // Clear previous indicators
    this.clearDropIndicators();

    // Add drop indicator to the target row using component's DOM context
    if (event.target instanceof HTMLElement) {
      const row = event.target.closest("tr");
      if (row && this.tableBody && this.tableBody.nativeElement.contains(row)) {
        row.classList.add("drop-indicator");
      }
    }
  }

  onDragLeave(event: DragEvent) {
    // Only clear if we're leaving the row entirely (not just moving between cells)
    if (event.target instanceof HTMLElement) {
      const row = event.target.closest("tr");
      if (
        row &&
        !row.contains(event.relatedTarget as Node) &&
        this.tableBody &&
        this.tableBody.nativeElement.contains(row)
      ) {
        row.classList.remove("drop-indicator");
      }
    }
  }

  onDrop(event: DragEvent, targetItem: PlaylistItem) {
    event.preventDefault();

    if (this.isStackEditingDisabled) {
      return;
    }
    if (!this.draggingItem || this.draggingItem.id === targetItem.id) {
      return;
    }

    // Get the current indices
    const draggingIndex = this.playlistItems.findIndex(
      (item) => item.id === this.draggingItem.id,
    );
    const targetIndex = this.playlistItems.findIndex(
      (item) => item.id === targetItem.id,
    );

    if (draggingIndex === -1 || targetIndex === -1) {
      return;
    }

    // Reorder the playlist items
    const reorderedItems = [...this.playlistItems];
    const [draggedItem] = reorderedItems.splice(draggingIndex, 1);
    reorderedItems.splice(targetIndex, 0, draggedItem);

    this.playlistItems = reorderedItems;

    // Save the new order to the data structure
    this.saveAssetsOrder();

    // Clear drop indicators immediately
    this.clearDropIndicators();

    // Trigger change detection first
    this._changeDetectorRef.detectChanges();

    // Clear again after change detection to ensure all effects are removed
    setTimeout(() => {
      this.clearDropIndicators();
      this._changeDetectorRef.detectChanges();
    }, 50);
  }

  private saveAssetsOrder() {
    const data = this.vm.data as assetBlockItemExtendedData | null;
    if (!data?.assets) return;
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);

    // Create new assetsOrder object
    const newAssetsOrder: { [key: string]: number } = {};
    this.playlistItems.forEach((item, index) => {
      newAssetsOrder[item.id] = index;
    });

    // Update the extended data
    extendedData.assetsOrder = newAssetsOrder;

    // Update the main data structure
    if (!this.vm.data[this.vm.uid]) {
      this.vm.data[this.vm.uid] = {} as any;
    }
    this.vm.data[this.vm.uid].assetsOrder = newAssetsOrder;
    this.vm.updateBlockExtendedData(this.broadcastUid);
  }

  private changeBaseLayerVolume(itemUid: string, volume: number) {
    const data = this.vm.data as assetBlockItemExtendedData | null;
    if (!data?.assets) return;
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);

    // Initialize the dictionary if it doesn't exist
    if (!extendedData.assetsControlBaseLayerVolume) {
      extendedData.assetsControlBaseLayerVolume = {};
    }

    extendedData.assetsControlBaseLayerVolume[itemUid] = volume;
    this.vm.updateBlockExtendedData(this.broadcastUid);
  }

  isItemMuted(itemId: string): boolean {
    const data = this.vm.data as assetBlockItemExtendedData | null;
    if (!data?.assets) {
      return false;
    }
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);

    // Handle cases where assetsControlBaseLayerVolume might be empty or undefined
    if (
      !extendedData.assetsControlBaseLayerVolume ||
      Object.keys(extendedData.assetsControlBaseLayerVolume).length === 0
    ) {
      return false; // Default to unmuted if no volume control data exists
    }

    // Check if the item exists in the volume control dictionary and is set to 0 (muted)
    return extendedData.assetsControlBaseLayerVolume[itemId] === 0;
  }

  onMuteAudioChange(isMuted: boolean, itemId: string): void {
    if (this.isStackEditingDisabled) {
      return;
    }
    const volume = isMuted ? 0 : 1;
    this.changeBaseLayerVolume(itemId, volume);
  }

  areAllItemsMuted(): boolean {
    if (!this.playlistItems || this.playlistItems.length === 0) {
      return false;
    }
    return this.playlistItems.every((item) => this.isItemMuted(item.id));
  }

  onMuteAllAudioChange(isMuted: boolean): void {
    if (this.isStackEditingDisabled) {
      return;
    }
    // Call onMuteAudioChange for each item
    this.playlistItems.forEach((item) => {
      this.onMuteAudioChange(isMuted, item.id);
    });
  }

  isItemLogoHidden(itemId: string): boolean {
    const data = this.vm.data as assetBlockItemExtendedData | null;
    if (!data?.assets) return true; // Default to hidden if no data
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);

    // Handle cases where assetsHideChannelLogo might be empty or undefined
    // Default to true (hidden) if not set
    if (
      !extendedData.assetsHideChannelLogo ||
      Object.keys(extendedData.assetsHideChannelLogo).length === 0
    ) {
      return true; // Default to hidden if no logo control data exists
    }

    // Check if the item exists in the hide logo dictionary
    // If not found, default to true (hidden)
    if (!extendedData.assetsHideChannelLogo.hasOwnProperty(itemId)) {
      return true;
    }

    return extendedData.assetsHideChannelLogo[itemId] === true;
  }

  onHideLogoChange(isHidden: boolean, itemId: string): void {
    if (this.isStackEditingDisabled) {
      return;
    }
    const data = this.vm.data as assetBlockItemExtendedData | null;
    if (!data?.assets) return;
    const extendedData = data.getBroadcastAssetItem(this.vm.uid);

    // Initialize the dictionary if it doesn't exist
    if (!extendedData.assetsHideChannelLogo) {
      extendedData.assetsHideChannelLogo = {};
    }

    extendedData.assetsHideChannelLogo[itemId] = isHidden;
    this.vm.updateBlockExtendedData(this.broadcastUid);
  }

  areAllItemsLogoHidden(): boolean {
    if (!this.playlistItems || this.playlistItems.length === 0) {
      return true; // Default to all hidden if no items
    }
    return this.playlistItems.every((item) => this.isItemLogoHidden(item.id));
  }

  onHideAllLogoChange(isHidden: boolean): void {
    if (this.isStackEditingDisabled) {
      return;
    }
    // Call onHideLogoChange for each item
    this.playlistItems.forEach((item) => {
      this.onHideLogoChange(isHidden, item.id);
    });
  }

  getTotalDuration(): string {
    // Calculate total duration from playlist items
    const totalMs = this.playlistItems.reduce((total, item) => {
      const duration = this.parseDuration(item.duration);
      return total + duration;
    }, 0);

    return this.formatDuration(totalMs);
  }

  getRowClass(item: PlaylistItem, index: number): string {
    if (this.vm.isStack && index === this.stackIndex) return "current-row";
    if (item.isSelected) return "selected-row";
    if (this.draggingItem && this.draggingItem.id === item.id)
      return "dragging";
    return "";
  }

  selectItem(item: PlaylistItem) {
    if (!item) {
      return;
    }
    // Clear previous selections
    this.playlistItems.forEach((i) => (i.isSelected = false));
    // Select current item
    item.isSelected = true;
    this.selectedItem = item;
  }

  private parseDuration(duration: string): number {
    const parts = duration.split(":");
    if (parts.length === 3) {
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      const seconds = parseFloat(parts[2].replace(",", ".")) || 0;
      return (hours * 3600 + minutes * 60 + seconds) * 1000;
    }
    return 0;
  }

  private formatDuration(ms: number): string {
    const totalSeconds = Math.ceil(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  private clearDropIndicators() {
    // Use the component's specific DOM context instead of global document
    if (this.tableBody && this.tableBody.nativeElement) {
      const rows = this.tableBody.nativeElement.querySelectorAll("tr");
      rows.forEach((row) => {
        row.classList.remove("drop-indicator");
        // Also remove any inline styles that might have been added
        row.removeAttribute("style");
      });
    }
  }

  // ─── Group authoring ──────────────────────────────────────────────────────

  get columnCount(): number {
    return this.vm?.isStack ? 7 : 3;
  }

  // Context menu state
  showGroupContextMenu = false;
  groupContextMenuPosition = { x: 0, y: 0 };
  groupContextMenuTargetId: string | null = null;

  private getGroupsMap(): { [assetId: string]: AssetGroupEntry } {
    const data = this.vm?.data as assetBlockItemExtendedData | null;
    if (!data) return {};
    const ed = data.getBroadcastAssetItem(this.vm.uid);
    if (!ed.assetGroups) ed.assetGroups = {};
    return ed.assetGroups;
  }

  isGroupHead(itemId: string): boolean {
    return !!this.getGroupsMap()[itemId];
  }

  getGroupLabel(itemId: string): string {
    return this.getGroupsMap()[itemId]?.label ?? '';
  }

  getGroupColor(itemId: string): string {
    const entry = this.getGroupsMap()[itemId];
    if (!entry) return 'transparent';
    return GROUP_COLORS[entry.colorIndex % GROUP_COLORS.length];
  }

  getRowGroupColor(itemId: string): string {
    const groups = this.getGroupsMap();
    const idx = this.playlistItems.findIndex((i) => i.id === itemId);
    for (let i = idx; i >= 0; i--) {
      const entry = groups[this.playlistItems[i]?.id];
      if (entry) return GROUP_COLORS[entry.colorIndex % GROUP_COLORS.length];
    }
    return 'transparent';
  }

  isInGroup(itemId: string): boolean {
    const groups = this.getGroupsMap();
    const idx = this.playlistItems.findIndex((i) => i.id === itemId);
    for (let i = idx; i >= 0; i--) {
      const entry = groups[this.playlistItems[i]?.id];
      if (entry) {
        if (entry.endAssetId) {
          const endIdx = this.playlistItems.findIndex(
            (item) => item.id === entry.endAssetId,
          );
          return endIdx === -1 || idx <= endIdx;
        }
        return true;
      }
    }
    return false;
  }

  isGroupEnd(itemId: string): boolean {
    const groups = this.getGroupsMap();
    const idx = this.playlistItems.findIndex((i) => i.id === itemId);
    for (let i = idx; i >= 0; i--) {
      const entry = groups[this.playlistItems[i]?.id];
      if (entry) {
        // Explicit end marker
        if (entry.endAssetId === itemId) return true;
        // Implicit end: group has no explicit end and the very next item
        // starts a new group — so this item is the last in its group.
        if (!entry.endAssetId) {
          const nextItem = this.playlistItems[idx + 1];
          return !!nextItem && !!groups[nextItem.id];
        }
        return false;
      }
    }
    return false;
  }

  private findGroupEntryForItem(itemId: string): AssetGroupEntry | null {
    const groups = this.getGroupsMap();
    const idx = this.playlistItems.findIndex((i) => i.id === itemId);
    for (let i = idx; i >= 0; i--) {
      const entry = groups[this.playlistItems[i]?.id];
      if (entry) return entry;
    }
    return null;
  }

  endGroupHere(itemId: string): void {
    const entry = this.findGroupEntryForItem(itemId);
    if (entry) {
      entry.endAssetId = itemId;
      this.vm.updateBlockExtendedData(this.broadcastUid);
      this._changeDetectorRef.detectChanges();
    }
  }

  removeGroupEnd(itemId: string): void {
    const entry = this.findGroupEntryForItem(itemId);
    if (entry && entry.endAssetId === itemId) {
      delete entry.endAssetId;
      this.vm.updateBlockExtendedData(this.broadcastUid);
      this._changeDetectorRef.detectChanges();
    }
  }

  private nextColorIndex(): number {
    const groups = this.getGroupsMap();
    const usedIndices = Object.values(groups).map((g) => g.colorIndex);
    for (let i = 0; i < GROUP_COLORS.length; i++) {
      if (!usedIndices.includes(i)) return i;
    }
    return usedIndices.length % GROUP_COLORS.length;
  }

  startGroup(itemId: string): void {
    const groups = this.getGroupsMap();
    groups[itemId] = { label: '', colorIndex: this.nextColorIndex() };
    this.vm.updateBlockExtendedData(this.broadcastUid);
    this._changeDetectorRef.detectChanges();
  }

  removeGroup(itemId: string): void {
    const groups = this.getGroupsMap();
    delete groups[itemId];
    this.vm.updateBlockExtendedData(this.broadcastUid);
    this._changeDetectorRef.detectChanges();
  }

  updateGroupLabelInMemory(itemId: string, label: string): void {
    const groups = this.getGroupsMap();
    if (groups[itemId]) {
      groups[itemId].label = label;
      // No save yet — just update the model so [value] binding doesn't reset
      // the input on the next change-detection cycle.
    }
  }

  saveGroupLabel(itemId: string): void {
    const groups = this.getGroupsMap();
    if (groups[itemId]) {
      this.vm.updateBlockExtendedData(this.broadcastUid);
    }
  }

  setGroupLabel(itemId: string, label: string): void {
    this.updateGroupLabelInMemory(itemId, label);
    this.saveGroupLabel(itemId);
  }

  cycleGroupColor(itemId: string): void {
    const groups = this.getGroupsMap();
    if (groups[itemId]) {
      groups[itemId].colorIndex =
        (groups[itemId].colorIndex + 1) % GROUP_COLORS.length;
      this.vm.updateBlockExtendedData(this.broadcastUid);
      this._changeDetectorRef.detectChanges();
    }
  }

  onRowContextMenu(event: MouseEvent, item: PlaylistItem): void {
    if (!this.vm?.isStack) return;
    event.preventDefault();
    this.groupContextMenuTargetId = item.id;
    this.groupContextMenuPosition = { x: event.clientX, y: event.clientY };
    this.showGroupContextMenu = true;
  }

  onStartGroupClick(): void {
    if (this.groupContextMenuTargetId) {
      this.startGroup(this.groupContextMenuTargetId);
    }
    this.hideGroupContextMenu();
  }

  onEndGroupHereClick(): void {
    if (this.groupContextMenuTargetId) {
      this.endGroupHere(this.groupContextMenuTargetId);
    }
    this.hideGroupContextMenu();
  }

  onRemoveGroupEndClick(): void {
    if (this.groupContextMenuTargetId) {
      this.removeGroupEnd(this.groupContextMenuTargetId);
    }
    this.hideGroupContextMenu();
  }

  onRemoveGroupFromContextMenu(): void {
    if (this.groupContextMenuTargetId) {
      this.removeGroup(this.groupContextMenuTargetId);
    }
    this.hideGroupContextMenu();
  }

  hideGroupContextMenu(): void {
    this.showGroupContextMenu = false;
    this.groupContextMenuTargetId = null;
  }

}
